<template>
  <nav :aria-label="$t.utilityNavigationLabel" class="text-sm lh-3">
    <client-only>
      <vf-link
        v-if="$feature.showSignInLinkMobileOnTop && !auth.loggedIn"
        class="lg:hidden"
        role="button"
        to="/sign-in"
        variant="quiet"
        @click.prevent.capture="handleSignIn"
      >
        {{ $t.signIn }}
      </vf-link>
    </client-only>
    <template v-for="({ type, to, title }, key) in links" :key>
      <base-button
        v-if="type === 'chat' && $feature.enableChat"
        class="uppercase hover:underlined"
        data-chat="utility-nav"
        @click="handleChat(title)"
      >
        {{ title }}
      </base-button>
      <vf-link
        v-else-if="type === 'loyalty' && !user.isWranxOrIpaProfile && !user.creditedConsumerProfileType"
        :to="auth.loggedIn && user?.isLoyaltyMember ? '/account/loyalty' : '/xplr-pass'"
        variant="quiet"
        @click="handleClick(auth.loggedIn ? $t.loyaltyUtilityNavLabel : $t.loyaltyUtilityNavGuestLabel)"
      >
        {{ auth.loggedIn ? user?.isLoyaltyMember ? $t.loyaltyUtilityNavLabel : $t.loyaltyUtilityNavGuestLabel
          : $t.loyaltyUtilityNavGuestLabel }}
      </vf-link>
      <vf-location-selector v-else-if="type === 'location-selector'" @click="handleClick($t.locationSelector)" />
      <slot v-else-if="to === '/help'" name="help">
        <vf-link :to variant="quiet" @click="handleClick(title)">
          {{ title }}
        </vf-link>
      </slot>
      <vf-link
        v-else-if="!['chat', 'loyalty', 'location-selector'].includes(type ?? '')"
        :to
        variant="quiet"
        @click="handleClick(title)"
      >
        {{ title }}
      </vf-link>
    </template>
    <client-only>
      <template v-if="auth.loggedIn">
        <vf-link to="/account" variant="quiet" @click="handleClick($t.myAccount)">
          {{ $t.myAccount }}
          <slot name="loyalty" />
        </vf-link>
        <base-button class="hover:underlined" @click="handleSignOut">
          {{ $t.signOut }}
        </base-button>
      </template>
      <template v-else>
        <vf-link
          :class="{ '<lg:hidden': !$feature.showSignInLinkMobileOnTop }"
          role="button"
          to="/sign-in"
          variant="quiet"
          @click.prevent.capture="handleSignIn"
        >
          {{ $t.signIn }}
        </vf-link>
      </template>
    </client-only>
    <loyalty-rewards-preview-tile
      v-if="$feature.showLoyaltyRewardsInUtilityNav && user.isLoyaltyMember && !user.isWranxOrIpaProfile"
      class="mx-2 <lg:hidden"
      close-on-leave
      gtm-title="loyalty"
      open-on-hover
      to="/account/loyalty"
    />
  </nav>
</template>

<script lang="ts" setup>
import type { NavigationLink } from '#types/navigation'

export type Props = {
  links: NavigationLink[]
}

export type Emits = {
  /**
   * Emits when a navigation item is clicked
   */
  click: []
}

const { links } = defineProps<Props>()
const emit = defineEmits<Emits>()

const auth = useAuthStore()
const user = useUserStore()
const { $chat, $feature, $gtm, $t } = useNuxtApp()
const { DialogSignIn } = useDialogsStore()

const handleClick = (title: string) => {
  emit('click')
  $gtm.push('topNav.onClickUtilityLink', capitalize(title))
}

const handleChat = (title: string) => {
  handleClick(title)
  $chat.open({ eventLabel: title, buttonLocation: 'utility-nav' })
}

const handleSignIn = async () => {
  handleClick($t.signIn)

  // Wait for the megamenu to close and unlock the body scroll,
  // otherwise reka will store the current overflow: hidden state,
  // and restore it after closing the sign in dialog
  await nextTick()

  DialogSignIn.open({
    formLocation: 'modal:single:header:none'
  })
}

const handleSignOut = () => {
  handleClick($t.signOut)
  auth.logout()
}
</script>
